import type {
  PraxaDesktopConversationMutationAttentionReason,
  PraxaDesktopConversationMutationResult,
  PraxaDesktopConversationMutationState,
  PraxaDesktopConversationSummary,
  PraxaDesktopResourceIndexState,
} from "@praxa/platform-contracts";

export function applyConversationMutationResult(
  state: PraxaDesktopResourceIndexState | null,
  result: PraxaDesktopConversationMutationResult,
): PraxaDesktopResourceIndexState | null {
  if (
    result.status !== "applied" ||
    state === null ||
    state.workspaceKind !== "personal" ||
    state.workspaceId !== "personal"
  ) {
    return state;
  }

  const itemIndex = state.conversations.findIndex(
    (conversation) => conversation.conversationId === result.conversationId,
  );
  const conversations =
    itemIndex === -1
      ? [result.item, ...state.conversations]
      : state.conversations.map((conversation, index) =>
          index === itemIndex ? result.item : conversation,
        );

  return {
    ...state,
    conversations,
  };
}

export function applyConversationMutationStateResult(
  state: PraxaDesktopConversationMutationState | null,
  result: PraxaDesktopConversationMutationResult,
): PraxaDesktopConversationMutationState | null {
  if (state === null || result.status === "rejected" || result.status === "applied") return state;

  if (result.status === "queued") {
    return {
      ...state,
      status: state.status === "offline" ? "offline" : "queued",
      pendingConversationIds: state.pendingConversationIds.includes(result.conversationId)
        ? state.pendingConversationIds
        : [...state.pendingConversationIds, result.conversationId],
      attention: state.attention.filter(
        (attention) => attention.conversationId !== result.conversationId,
      ),
    };
  }

  return {
    ...state,
    status: "attention",
    pendingConversationIds: state.pendingConversationIds.filter(
      (conversationId) => conversationId !== result.conversationId,
    ),
    attention: [
      ...state.attention.filter((attention) => attention.conversationId !== result.conversationId),
      {
        conversationId: result.conversationId,
        reason: result.reason,
      },
    ],
  };
}

export function isConversationMutationAccepted(
  result: PraxaDesktopConversationMutationResult | null,
): boolean {
  return result?.status === "applied" || result?.status === "queued";
}

export function isConversationMutationPending(
  state: PraxaDesktopConversationMutationState | null,
  conversationId: string,
): boolean {
  return state?.pendingConversationIds.includes(conversationId) ?? false;
}

export function canMutateConversation(
  conversation: PraxaDesktopConversationSummary,
  state: PraxaDesktopConversationMutationState | null,
): boolean {
  return (
    conversation.ownedByCaller &&
    !conversation.readOnly &&
    !isConversationMutationPending(state, conversation.conversationId)
  );
}

export function conversationMutationAttentionMessage(
  reason: PraxaDesktopConversationMutationAttentionReason,
): string {
  switch (reason) {
    case "conflict":
      return "This conversation changed elsewhere. Discard the saved change, then try again.";
    case "expired":
      return "This saved change expired before it could sync. Discard it to clear the queue.";
    case "not_found":
      return "This conversation no longer exists. Discard its saved change to clear the queue.";
    case "rejected":
      return "Praxa refused this saved change. Discard it, then try a different value.";
  }
}

export function conversationMutationResultMessage(
  result: PraxaDesktopConversationMutationResult,
): string | null {
  if (result.status === "applied" || result.status === "queued") return null;
  if (result.status === "attention") {
    return conversationMutationAttentionMessage(result.reason);
  }

  switch (result.reason) {
    case "busy":
      return "Praxa is finishing another saved change. Your entry is still here; try again.";
    case "invalid":
      return "Use a conversation title between 1 and 80 characters.";
    case "organization":
      return "Organization channels are read-only in the desktop app.";
    case "read_only":
      return "Only conversations you own can be changed here.";
    case "secure_storage":
      return "This change wasn’t saved because secure desktop storage is unavailable.";
    case "unavailable":
      return "Conversation changes are unavailable right now. Your entry is still here; try again.";
  }
}
